import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Lightweight polled counter hook for the bottom navigation badges.
 *
 *   - `dmUnread`     : number of DM conversations where the counterpart wrote
 *                      something AFTER my `last_read_at`.
 *   - `squadPending` : number of pending squad invites addressed to me.
 *
 * Polls every 30 s while a user is authenticated. The badges are cosmetic
 * hints; we never block UI on this.
 */
export default function useNavBadges() {
  const { user } = useAuth();
  const [dmUnread, setDmUnread] = useState(0);
  const [squadPending, setSquadPending] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) {
      setDmUnread(0);
      setSquadPending(0);
      return;
    }
    try {
      const [dm, sq] = await Promise.allSettled([
        api.get("/dm/conversations"),
        api.get("/squads/invites/pending").catch(() => ({ data: { items: [] } })),
      ]);
      if (dm.status === "fulfilled") {
        const items = dm.value?.data?.items || [];
        const unread = items.filter((c) => {
          const last = c.last_message_at;
          const read = c.last_read_at;
          if (!last) return false;
          if (!read) return true;
          return new Date(last).getTime() > new Date(read).getTime();
        }).length;
        setDmUnread(unread);
      }
      if (sq.status === "fulfilled") {
        const items = sq.value?.data?.items || [];
        setSquadPending(items.length);
      }
    } catch (_e) { /* swallow */ }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return;
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh, user]);

  return { dmUnread, squadPending, refresh };
}
